import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import NewsFilter from "./components/NewsFilter";

const PAGE_SIZE = 5;

function inRange(dateStr: string, range: string): boolean {
  if (range === "全部") return true;
  const target = new Date(dateStr).getTime();
  const now = Date.now();
  const oneYear = 365 * 24 * 60 * 60 * 1000;
  if (range === "近一年") return now - target <= oneYear;
  if (range === "近两年") return now - target <= 2 * oneYear;
  return now - target > 2 * oneYear;
}

export default function News() {
  const [searchParams] = useSearchParams();
  usePageMeta(
    "濑户环奈最新新闻动态 - 濑户环奈官网",
    "濑户环奈最新娱乐新闻、官方公告、活动报道与媒体采访汇总，第一时间了解她的最新动态。",
  );

  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [category, setCategory] = useState("全部");
  const [range, setRange] = useState("全部");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setKeyword(q);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [keyword, category, range]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return newsList
      .filter((item) => {
        const matchKeyword =
          !kw ||
          item.title.toLowerCase().includes(kw) ||
          item.summary.toLowerCase().includes(kw) ||
          item.category.toLowerCase().includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw));
        const matchCategory = category === "全部" || item.category === category;
        return matchKeyword && matchCategory && inRange(item.date, range);
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [keyword, category, range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setKeyword("");
    setCategory("全部");
    setRange("全部");
  };

  const filterPanel = (
    <NewsFilter
      category={category}
      onCategoryChange={setCategory}
      range={range}
      onRangeChange={setRange}
      onReset={reset}
      resultCount={filtered.length}
    />
  );

  return (
    <PageShell>
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "新闻动态" }]} />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4 mb-3">
              濑户环奈最新动态
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              汇总官方公告、活动报道与媒体采访，按类别与时间区间筛选，掌握濑户环奈的最新资讯。
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 p-5">
              {filterPanel}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5 flex-1 max-w-md focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-foreground-400 text-base"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索新闻关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                {keyword && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清空搜索"
                    className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </div>

            {paged.length > 0 ? (
              <div className="flex flex-col gap-4">
                {paged.map((item) => (
                  <NewsCard key={item.id} item={item} keyword={keyword} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-2xl mb-4">
                  <i className="ri-search-eye-line"></i>
                </span>
                <p className="text-sm text-foreground-500">没有找到符合条件的新闻，试试其他关键词或筛选条件。</p>
              </div>
            )}

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFiltersOpen(false)}
          ></div>
          <div className="relative ml-auto w-[85%] max-w-sm h-full bg-background-50 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-5">{filterPanel}</div>
            <div className="sticky bottom-0 p-4 bg-background-50 border-t border-background-200">
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看 {filtered.length} 条动态
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}